import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from './api';
import { ArrowLeft, CheckCircle2, Circle, Clock, FileText, MessageSquare, Send, Sparkles, User, Phone, Mail, Home, Plus, DollarSign } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { CancelledPill } from './TasksPage';
import { OwnerChip, useStaff } from './kit/OwnerChip';
import { formatPhone } from './shared/phone';
import { LOAN_TYPE_LABELS, labelFor } from './shared/labels';
import { formatDateTime } from './kit/time';
import LeadCommissionCard from './LeadCommissionCard';
import LeadInviteDialog from './LeadInviteDialog';

const stages = ['new','contacted','qualified','application','processing','approved','closed_won','closed_lost'];
const stageLabels = { new: 'New', contacted: 'Contacted', qualified: 'Qualified', application: 'Application', processing: 'Processing', approved: 'Approved', closed_won: 'Closed Won', closed_lost: 'Lost' };

const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const staff = useStaff();

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}/timeline`, { params: showCancelled ? { include_cancelled: 1 } : {} });
      setData(data);
    }
    catch { toast({ title: 'Lead not found' }); navigate('/portal/leads'); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [leadId, showCancelled]);

  const advance = async (newStatus) => {
    try {
      const { data: r } = await api.post(`/leads/${leadId}/advance?new_status=${newStatus}`);
      const parts = [];
      if (r.auto_tasks) parts.push(`${r.auto_tasks} task${r.auto_tasks === 1 ? '' : 's'} auto-generated`);
      if (r.revived) parts.push(`${r.revived} revived`);
      if (r.cancelled) parts.push(`${r.cancelled} cancelled`);
      toast({ title: 'Stage updated', description: parts.length ? parts.join(' · ') : `Now ${stageLabels[newStatus] || newStatus}` });
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Error' }); }
  };

  // Ticket 116: reassign from the header chip.
  const reassign = async (ownerId) => {
    try {
      const { data: r } = await api.post(`/leads/${leadId}/owner`, { owner_id: ownerId });
      const moved = r?.auto_tasks_moved || 0;
      toast({ title: `Reassigned to ${r?.owner_name || 'teammate'}`, description: moved ? `${moved} open task${moved === 1 ? '' : 's'} moved along` : undefined });
      load();
    } catch (e) { toast({ title: 'Reassign failed', description: errorMessage(e) }); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post('/notes', { content: note, lead_id: leadId });
    setNote(''); load();
  };

  if (!data) return <div className="p-10 text-slate-500">Loading...</div>;
  const { lead, milestones, activities, tasks, notes, documents } = data;

  return (
    <div className="p-4 md:p-8">
      <Link to="/portal/leads" className="inline-flex items-center gap-2 text-sm mb-4 hover:opacity-70" style={{ color: '#070B5F' }}>
        <ArrowLeft size={14} /> Back to Leads
      </Link>

      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-100 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 sm:w-16 sm:h-16 shrink-0 rounded-full flex items-center justify-center font-serif font-bold text-2xl" style={{ background: '#070B5F', color: '#A6E56C' }}>{lead.name[0]?.toUpperCase()}</div>
            <div className="min-w-0">
              <h1 className="font-serif text-2xl sm:text-3xl font-bold break-words" style={{ color: '#070B5F' }}>{lead.name}</h1>
              <div className="text-sm text-slate-500 flex flex-wrap items-center gap-3 mt-1">
                {lead.email && <span className="flex items-center gap-1"><Mail size={12} />{lead.email}</span>}
                {lead.phone && <span className="flex items-center gap-1"><Phone size={12} />{formatPhone(lead.phone)}</span>}
                {lead.property_address && <span className="flex items-center gap-1"><Home size={12} />{lead.property_address}</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap md:flex-nowrap items-start gap-4">
            <div className="md:text-right">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Owner</div>
              <OwnerChip userId={lead.owner_id} name={lead.owner_name} staff={staff} onChange={reassign} showName size="md" testId="lead-owner-select" />
            </div>
            <div className="md:text-right">
              <div className="text-xs uppercase tracking-widest text-slate-500">Loan Amount</div>
              <div className="font-serif text-2xl font-bold" style={{ color: '#6BA82E' }}>{fmt$(lead.loan_amount)}</div>
              <div className="text-xs text-slate-500 mt-0.5">{labelFor(LOAN_TYPE_LABELS, lead.loan_type)}</div>
            </div>
            <button onClick={() => setInviteOpen(true)} className="btn-outline"><Plus size={14} /> Invite</button>
          </div>
        </div>

        {/* Stage advance */}
        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Current Stage: <span className="font-bold" style={{ color: '#070B5F' }}>{stageLabels[lead.status]}</span></div>
          <div className="flex flex-wrap gap-2">
            {stages.map(s => (
              <button key={s} onClick={() => advance(s)} disabled={s === lead.status} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${s === lead.status ? 'text-white cursor-default' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} style={s === lead.status ? { background: '#070B5F' } : {}}>
                {stageLabels[s]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Milestones */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mb-6">
        <div className="font-serif font-semibold text-lg mb-4" style={{ color: '#070B5F' }}>Loan Milestones</div>
        <div className="flex flex-wrap gap-3">
          {milestones.map((m, i) => (
            <div key={m.key} className="flex items-center gap-2 flex-1 min-w-[150px]">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0`} style={{ background: m.reached ? '#6BA82E' : '#e2e8f0', color: m.reached ? 'white' : '#94a3b8' }}>
                {m.reached ? <CheckCircle2 size={16} /> : <Circle size={16} />}
              </div>
              <div className="min-w-0">
                <div className={`text-sm font-semibold ${m.reached ? '' : 'text-slate-400'}`} style={m.reached ? { color: '#070B5F' } : {}}>{m.label}</div>
              </div>
              {i < milestones.length - 1 && <div className="flex-1 h-px bg-slate-200 hidden md:block" />}
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Activity */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="font-serif font-semibold text-lg mb-4" style={{ color: '#070B5F' }}>Activity Feed</div>
          {activities.length === 0 && <div className="text-slate-400 text-sm">No activity yet.</div>}
          <div className="space-y-3">
            {activities.map((a) => (
              <div key={a.activity_id} className="flex gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#F3FAE8' }}>
                  <Sparkles size={13} style={{ color: '#6BA82E' }} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold" style={{ color: '#070B5F' }}>{a.title}</div>
                  {a.description && <div className="text-xs text-slate-600 mt-0.5">{a.description}</div>}
                  <div className="text-[10px] text-slate-400 uppercase mt-1">{formatDateTime(a.created_at)} • {a.kind.replace('_',' ')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Tasks summary */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="font-serif font-semibold" style={{ color: '#070B5F' }}>Tasks ({tasks.filter(t => !t.completed && !t.cancelled_at).length} open)</div>
              <button type="button" onClick={() => setShowCancelled(v => !v)} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="toggle-cancelled">
                {showCancelled ? 'Hide cancelled' : 'Show cancelled'}
              </button>
            </div>
            {tasks.length === 0 && <div className="text-xs text-slate-400">No tasks.</div>}
            {tasks.slice(0, showCancelled ? 10 : 5).map(t => {
              const muted = t.completed || t.cancelled_at;
              return (
                <div key={t.task_id} className="flex items-start gap-2 py-1.5">
                  {t.completed ? <CheckCircle2 size={14} style={{ color: '#6BA82E' }} /> : <Circle size={14} className="text-slate-300" />}
                  <div className={`text-sm flex-1 ${muted ? 'line-through text-slate-400' : ''}`} style={muted ? {} : { color: '#0f172a' }}>
                    {t.title}
                    {t.cancelled_at && <CancelledPill reason={t.cancelled_reason} />}
                  </div>
                </div>
              );
            })}
          </div>

          <LeadCommissionCard key={`${lead.commission_type}-${lead.commission_bps}-${lead.commission_percent}-${lead.commission_flat}`} lead={lead} onSaved={load} />

          {/* Notes */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <div className="font-serif font-semibold mb-3" style={{ color: '#070B5F' }}>Notes</div>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." className="mb-2" />
            <button onClick={addNote} disabled={!note.trim()} className="btn-gold w-full justify-center py-2 text-xs">Add Note</button>
            <div className="mt-3 space-y-2">
              {notes.slice(0, 5).map(n => (
                <div key={n.note_id} className="text-xs bg-slate-50 p-2 rounded-lg">
                  <div className="text-slate-700">{n.content}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{formatDateTime(n.created_at)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <div className="font-serif font-semibold mb-3" style={{ color: '#070B5F' }}>Documents ({documents.length})</div>
            {documents.length === 0 && <div className="text-xs text-slate-400">No documents.</div>}
            {documents.slice(0, 6).map(d => (
              <div key={d.document_id} className="flex items-center gap-2 py-1.5">
                <FileText size={13} style={{ color: '#6BA82E' }} />
                <div className="text-xs flex-1 truncate" style={{ color: '#070B5F' }}>{d.filename}</div>
                <div className="text-[10px] text-slate-400 uppercase">{d.file_type}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <LeadInviteDialog leadId={leadId} lead={lead} open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}
